import NavbarV2 from '../../components/navbarV2/NavbarV2';
import './Admin.css';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
    FaChartLine,
    FaLink,
    FaCommentDots,
    FaSyncAlt,
    FaExclamationTriangle,
} from 'react-icons/fa';

const Admin = () => {
    const [popups, setPopups] = useState(null);
    const [popupMessage, setPopupMessage] = useState('');
    const [bannerTitleVal, setBannerTitleVal] = useState('');
    const [bannerBody, setBannerBody] = useState('');
    const [xeroStatus, setXeroStatus] = useState(null);
    const [xeroStatusLoading, setXeroStatusLoading] = useState(true);
    const navigate = useNavigate();

    const refreshXeroStatus = () => {
        setXeroStatusLoading(true);
        fetch(`${process.env.REACT_APP_API}/api/xeroStatus`, {})
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                setXeroStatus(data);
            })
            .catch(() => {
                setXeroStatus(null);
            })
            .finally(() => {
                setXeroStatusLoading(false);
            });
    };

    useEffect(() => {
        if (localStorage.getItem('account') == null || localStorage.getItem('admin') != 'true') {
            navigate('/login');
        }
        fetch(`${process.env.REACT_APP_API}/api/getPopups`, {})
            .then((response) => {
                if (response.status === 200) {
                    return response.json();
                }
                return null;
            })
            .then((data) => {
                setPopups(data);
            })
            .catch(() => setPopups(null));

        refreshXeroStatus();
    }, []);

    useEffect(() => {
        if (popups && Array.isArray(popups) && popups.length >= 4) {
            setPopupMessage(popups[0]?.message ?? '');
            setBannerTitleVal(popups[2]?.message ?? '');
            setBannerBody(popups[3]?.message ?? '');
        }
    }, [popups]);

    const updatePopup = (e) => {
        e.preventDefault();

        fetch(`${process.env.REACT_APP_API}/api/updatePopup`, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: JSON.stringify({
                popup: popupMessage,
            }),
        }).then((response) => {
            if (response.status === 200) {
                toast.success('Changed popup!');
            } else if (response.status === 500) {
                toast.error('Server error.');
            }
        });
    };

    const updateBanner = (e) => {
        e.preventDefault();

        fetch(`${process.env.REACT_APP_API}/api/updateBanner`, {
            method: 'POST',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: JSON.stringify({
                title: bannerTitleVal,
                banner: bannerBody,
            }),
        }).then((response) => {
            if (response.status === 200) {
                toast.success('Changed Banner!');
            } else if (response.status === 500) {
                toast.error('Server error.');
            }
        });
    };

    const auth = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API}/api/getAuthUrl`, {});
            const data = await response.json();
            if (!response.ok) {
                toast.error(data?.error || `Could not start Xero login (${response.status})`);
                return;
            }
            if (typeof data !== 'string') {
                toast.error(data?.error || 'Unexpected response from server');
                return;
            }
            window.location.href = data;
        } catch {
            toast.error('Could not reach the server');
        }
    };

    return (
        <div className="v2-page admin-v2-page">
            <NavbarV2 />

            <main className="v2-container admin-v2-page__main">
                <header className="admin-v2-page__hero">
                    <h1 className="v2-h1">Admin</h1>
                    <p className="v2-muted admin-v2-page__lede">
                        Sales analytics, accounting links, and sitewide customer messages — aligned with Sicily V2.
                    </p>
                </header>

                <div className="admin-v2-page__cards">
                    <section className="v2-card admin-v2-card">
                        <h2 className="v2-card-title admin-v2-card__heading">
                            <FaChartLine className="admin-v2-card__icon" aria-hidden />
                            Sales insights
                        </h2>
                        <p className="v2-muted admin-v2-card__text">
                            View gross revenue and margin from shipping &amp; surcharges in the revenue dashboard.
                        </p>
                        <div className="v2-actions">
                            <button
                                type="button"
                                className="v2-btn v2-btn--primary"
                                onClick={() => navigate('/admin/revenue')}
                            >
                                Open revenue analytics
                            </button>
                        </div>
                    </section>

                    <section className="v2-card admin-v2-card">
                        <h2 className="v2-card-title admin-v2-card__heading">
                            <FaLink className="admin-v2-card__icon" aria-hidden />
                            Xero accounting
                        </h2>

                        {xeroStatusLoading && (
                            <p className="v2-muted admin-v2-card__text">Checking Xero connection…</p>
                        )}
                        {!xeroStatusLoading && !xeroStatus && (
                            <>
                                <p className="admin-v2-alert admin-v2-alert--danger admin-v2-card__text" role="status">
                                    <FaExclamationTriangle className="admin-v2-alert__icon" aria-hidden />
                                    Could not load Xero status (is the API running and REACT_APP_API set?).
                                </p>
                                <div className="v2-actions">
                                    <button
                                        type="button"
                                        className="v2-btn v2-btn--outline"
                                        onClick={() => refreshXeroStatus()}
                                    >
                                        <FaSyncAlt className="admin-v2-btn-icon" aria-hidden />
                                        Try again
                                    </button>
                                </div>
                            </>
                        )}
                        {!xeroStatusLoading && xeroStatus && !xeroStatus.configured && (
                            <p className="admin-v2-alert admin-v2-alert--danger admin-v2-card__text" role="status">
                                <FaExclamationTriangle className="admin-v2-alert__icon" aria-hidden />
                                Xero is not configured on the server (missing client credentials).
                            </p>
                        )}
                        {!xeroStatusLoading && xeroStatus && xeroStatus.configured && xeroStatus.connected && (
                            <>
                                <div className="admin-v2-alert admin-v2-alert--success admin-v2-card__text">
                                    <p className="admin-v2-alert__title">
                                        <FaLink className="admin-v2-alert__lead-icon" aria-hidden />
                                        Connected to Xero
                                    </p>
                                    {xeroStatus.organisationName && (
                                        <p className="admin-v2-alert__detail">
                                            Organisation: <strong>{xeroStatus.organisationName}</strong>
                                            {xeroStatus.organisations && xeroStatus.organisations.length > 1 && (
                                                <span className="admin-v2-alert__meta">
                                                    {' '}
                                                    (+{xeroStatus.organisations.length - 1} more)
                                                </span>
                                            )}
                                        </p>
                                    )}
                                    {xeroStatus.accessTokenExpiresAt && (
                                        <p className="admin-v2-alert__meta admin-v2-alert__meta--tight">
                                            Access token refreshes automatically; current access expires{' '}
                                            {new Date(xeroStatus.accessTokenExpiresAt).toLocaleString()}
                                        </p>
                                    )}
                                    {xeroStatus.statusWarning && (
                                        <p className="admin-v2-alert admin-v2-alert--warn admin-v2-alert--inline admin-v2-card__text">
                                            <FaExclamationTriangle className="admin-v2-alert__icon" aria-hidden />
                                            Could not verify with Xero — try reconnecting. ({xeroStatus.statusWarning})
                                        </p>
                                    )}
                                </div>
                                <p className="v2-muted admin-v2-card__text">
                                    Invoices and credit notes sync to this organisation. Reconnect if you change organisation or
                                    renew credentials.
                                </p>
                                <div className="v2-actions">
                                    <button type="button" className="v2-btn v2-btn--primary" onClick={() => auth()}>
                                        Reconnect to Xero
                                    </button>
                                    <button
                                        type="button"
                                        className="v2-btn v2-btn--outline"
                                        onClick={() => refreshXeroStatus()}
                                    >
                                        <FaSyncAlt className="admin-v2-btn-icon" aria-hidden />
                                        Refresh status
                                    </button>
                                </div>
                            </>
                        )}
                        {!xeroStatusLoading && xeroStatus && xeroStatus.configured && !xeroStatus.connected && (
                            <>
                                <p className="v2-muted admin-v2-card__text">
                                    Connect Xero so invoices and credit notes sync. You may need to sign in again if tokens
                                    expire.
                                </p>
                                <div className="v2-actions">
                                    <button type="button" className="v2-btn v2-btn--secondary" onClick={() => auth()}>
                                        Authenticate with Xero
                                    </button>
                                </div>
                            </>
                        )}
                    </section>

                    {popups && Array.isArray(popups) && popups.length >= 4 && (
                        <section className="v2-card admin-v2-card">
                            <h2 className="v2-card-title admin-v2-card__heading">
                                <FaCommentDots className="admin-v2-card__icon" aria-hidden />
                                Messages &amp; banner
                            </h2>

                            <form className="admin-v2-form" onSubmit={updatePopup}>
                                <div className="v2-field">
                                    <label htmlFor="admin-popup-msg">Popup</label>
                                    <textarea
                                        id="admin-popup-msg"
                                        name="popup"
                                        rows={4}
                                        value={popupMessage}
                                        onChange={(e) => setPopupMessage(e.target.value)}
                                    />
                                </div>
                                <div className="v2-actions">
                                    <button type="submit" className="v2-btn v2-btn--secondary">
                                        Save popup
                                    </button>
                                </div>
                            </form>

                            <hr className="v2-divider" />

                            <form className="admin-v2-form" onSubmit={updateBanner}>
                                <div className="v2-field">
                                    <label htmlFor="admin-banner-title">Banner title</label>
                                    <input
                                        id="admin-banner-title"
                                        name="title"
                                        type="text"
                                        value={bannerTitleVal}
                                        onChange={(e) => setBannerTitleVal(e.target.value)}
                                    />
                                </div>
                                <div className="v2-field">
                                    <label htmlFor="admin-banner-body">Banner message</label>
                                    <textarea
                                        id="admin-banner-body"
                                        name="banner"
                                        rows={4}
                                        value={bannerBody}
                                        onChange={(e) => setBannerBody(e.target.value)}
                                    />
                                </div>
                                <div className="v2-actions">
                                    <button type="submit" className="v2-btn v2-btn--secondary">
                                        Save banner
                                    </button>
                                </div>
                            </form>
                        </section>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Admin;
